import { useState, useEffect, useCallback } from 'react'
import {
  Award,
  Trophy,
  TrendingUp,
  Plus,
  Pencil,
  Trash2,
  Search,
  Loader2,
  AlertCircle,
  Leaf,
  Trash2 as TrashIcon,
  UtensilsCrossed,
  Users,
  Crown,
  Medal,
  X,
  Check,
  Image as ImageIcon,
  BarChart3,
} from 'lucide-react'
import { toast } from 'react-hot-toast'
import gamificationService, {
  type Badge,
  type Achievement,
  type ImpactStats,
  type LeaderboardEntry,
  type UserAchievements,
} from '../services/gamificationService'

type Tab = 'badges' | 'achievements' | 'impact' | 'users'

const TIERS = ['bronze', 'silver', 'gold', 'platinum'] as const

const GamificationPage = () => {
  const [activeTab, setActiveTab] = useState<Tab>('badges')

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Gamification</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          Gérez les badges, succès et suivez l'impact de la communauté
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-lg w-fit mb-6">
        {[
          { id: 'badges' as Tab, label: 'Badges', icon: Award },
          { id: 'achievements' as Tab, label: 'Succès', icon: Trophy },
          { id: 'impact' as Tab, label: 'Impact', icon: TrendingUp },
          { id: 'users' as Tab, label: 'Utilisateurs', icon: Users },
        ].map((tab) => {
          const Icon = tab.icon
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          )
        })}
      </div>

      {activeTab === 'badges' && <BadgesTab />}
      {activeTab === 'achievements' && <AchievementsTab />}
      {activeTab === 'impact' && <ImpactTab />}
      {activeTab === 'users' && <UserAchievementsTab />}
    </div>
  )
}

// ==================== Badges Tab ====================
const BadgesTab = () => {
  const [badges, setBadges] = useState<Badge[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingBadge, setEditingBadge] = useState<Badge | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    criteria_type: 'orders_count',
    criteria_value: 1,
    tier: 'bronze' as Badge['tier'],
  })
  const [saving, setSaving] = useState(false)

  const loadBadges = useCallback(async () => {
    try {
      const res = await gamificationService.getBadges()
      setBadges(res.data)
    } catch (err: any) {
      toast.error('Erreur lors du chargement des badges')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadBadges() }, [loadBadges])

  const openCreate = () => {
    setEditingBadge(null)
    setFormData({ name: '', description: '', criteria_type: 'orders_count', criteria_value: 1, tier: 'bronze' })
    setShowForm(true)
  }

  const openEdit = (badge: Badge) => {
    setEditingBadge(badge)
    setFormData({
      name: badge.name,
      description: badge.description,
      criteria_type: badge.criteria_type,
      criteria_value: badge.criteria_value,
      tier: badge.tier,
    })
    setShowForm(true)
  }

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Le nom est requis')
      return
    }
    setSaving(true)
    try {
      if (editingBadge) {
        await gamificationService.updateBadge(editingBadge.id, formData)
        toast.success('Badge mis à jour')
      } else {
        await gamificationService.createBadge(formData)
        toast.success('Badge créé')
      }
      setShowForm(false)
      setEditingBadge(null)
      loadBadges()
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Erreur lors de la sauvegarde')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm('Supprimer ce badge ?')) return
    try {
      await gamificationService.deleteBadge(id)
      toast.success('Badge supprimé')
      loadBadges()
    } catch (err: any) {
      toast.error('Erreur lors de la suppression')
    }
  }

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary-600" /></div>
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-gray-500">{badges.length} badge{badges.length > 1 ? 's' : ''}</p>
        <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium">
          <Plus className="w-4 h-4" />
          Nouveau badge
        </button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowForm(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                {editingBadge ? 'Modifier le badge' : 'Nouveau badge'}
              </h3>
              <button onClick={() => setShowForm(false)} className="p-1 text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nom</label>
                <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type de critère</label>
                  <select value={formData.criteria_type} onChange={(e) => setFormData({ ...formData, criteria_type: e.target.value })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100">
                    <option value="orders_count">Nombre de commandes</option>
                    <option value="points">Points total</option>
                    <option value="co2_saved">CO₂ économisé</option>
                    <option value="meals_rescued">Repas sauvés</option>
                    <option value="referrals">Parrainages</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Valeur du critère</label>
                  <input type="number" value={formData.criteria_value} onChange={(e) => setFormData({ ...formData, criteria_value: parseInt(e.target.value) || 0 })} min={1} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Palier</label>
                <select value={formData.tier} onChange={(e) => setFormData({ ...formData, tier: e.target.value as Badge['tier'] })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100">
                  {TIERS.map((tier) => (
                    <option key={tier} value={tier}>{tier.charAt(0).toUpperCase() + tier.slice(1)}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowForm(false)} className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium">Annuler</button>
              <button onClick={handleSave} disabled={saving} className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 text-sm font-medium flex items-center justify-center gap-2">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                {editingBadge ? 'Mettre à jour' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Badges Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {badges.map((badge) => (
          <div key={badge.id} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                  <Award className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">{badge.name}</h3>
                  <span className={`text-[10px] uppercase tracking-wider font-bold ${
                    badge.tier === 'gold' ? 'text-yellow-600' :
                    badge.tier === 'platinum' ? 'text-cyan-600' :
                    badge.tier === 'silver' ? 'text-slate-500' : 'text-amber-700'
                  }`}>{badge.tier}</span>
                </div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => openEdit(badge)} className="p-1.5 text-gray-400 hover:text-primary-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => handleDelete(badge.id)} className="p-1.5 text-gray-400 hover:text-red-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">{badge.description}</p>
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full">{badge.criteria_type}</span>
              <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-full">≥ {badge.criteria_value}</span>
            </div>
          </div>
        ))}
        {badges.length === 0 && (
          <div className="col-span-full text-center py-12 text-gray-400">
            <Award className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Aucun badge pour le moment</p>
          </div>
        )}
      </div>
    </div>
  )
}

// ==================== Achievements Tab ====================
const AchievementsTab = () => {
  const [achievements, setAchievements] = useState<Achievement[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingAchievement, setEditingAchievement] = useState<Achievement | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'environment',
    points: 10,
  })
  const [saving, setSaving] = useState(false)

  const loadAchievements = useCallback(async () => {
    try {
      const res = await gamificationService.getAchievements()
      setAchievements(res.data)
    } catch {
      toast.error('Erreur lors du chargement des succès')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadAchievements() }, [loadAchievements])

  const openCreate = () => {
    setEditingAchievement(null)
    setFormData({ name: '', description: '', category: 'environment', points: 10 })
    setShowForm(true)
  }

  const openEdit = (achievement: Achievement) => {
    setEditingAchievement(achievement)
    setFormData({
      name: achievement.name,
      description: achievement.description,
      category: achievement.category,
      points: achievement.points,
    })
    setShowForm(true)
  }

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Le nom est requis')
      return
    }
    setSaving(true)
    try {
      if (editingAchievement) {
        await gamificationService.updateAchievement(editingAchievement.id, formData)
        toast.success('Succès mis à jour')
      } else {
        await gamificationService.createAchievement(formData)
        toast.success('Succès créé')
      }
      setShowForm(false)
      setEditingAchievement(null)
      loadAchievements()
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Erreur lors de la sauvegarde')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm('Supprimer ce succès ?')) return
    try {
      await gamificationService.deleteAchievement(id)
      toast.success('Succès supprimé')
      loadAchievements()
    } catch {
      toast.error('Erreur lors de la suppression')
    }
  }

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary-600" /></div>
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-gray-500">{achievements.length} succès</p>
        <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium">
          <Plus className="w-4 h-4" />
          Nouveau succès
        </button>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowForm(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                {editingAchievement ? 'Modifier le succès' : 'Nouveau succès'}
              </h3>
              <button onClick={() => setShowForm(false)} className="p-1 text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nom</label>
                <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Catégorie</label>
                  <select value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100">
                    <option value="environment">Environnement</option>
                    <option value="social">Social</option>
                    <option value="engagement">Engagement</option>
                    <option value="discovery">Découverte</option>
                    <option value="loyalty">Fidélité</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Points</label>
                  <input type="number" value={formData.points} onChange={(e) => setFormData({ ...formData, points: parseInt(e.target.value) || 0 })} min={1} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100" />
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setShowForm(false)} className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium">Annuler</button>
              <button onClick={handleSave} disabled={saving} className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 text-sm font-medium flex items-center justify-center gap-2">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                {editingAchievement ? 'Mettre à jour' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {achievements.map((achievement) => (
          <div key={achievement.id} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                  <Trophy className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">{achievement.name}</h3>
                  <span className="text-xs text-gray-400">{achievement.category}</span>
                </div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => openEdit(achievement)} className="p-1.5 text-gray-400 hover:text-primary-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
                  <Pencil className="w-4 h-4" />
                </button>
                <button onClick={() => handleDelete(achievement.id)} className="p-1.5 text-gray-400 hover:text-red-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">{achievement.description}</p>
            <div className="flex items-center gap-2">
              <span className="px-2 py-1 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-xs font-medium rounded-full">
                +{achievement.points} pts
              </span>
            </div>
          </div>
        ))}
        {achievements.length === 0 && (
          <div className="col-span-full text-center py-12 text-gray-400">
            <Trophy className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Aucun succès pour le moment</p>
          </div>
        )}
      </div>
    </div>
  )
}

// ==================== Impact Tab ====================
const ImpactTab = () => {
  const [stats, setStats] = useState<ImpactStats | null>(null)
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const load = async () => {
      try {
        const [statsRes, leaderboardRes] = await Promise.all([
          gamificationService.getImpactStats(),
          gamificationService.getLeaderboard(),
        ])
        setStats(statsRes.data)
        setLeaderboard(leaderboardRes.data)
      } catch {
        toast.error('Erreur lors du chargement des données d\'impact')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary-600" /></div>
  }

  const statCards = [
    { label: 'CO₂ économisé', value: stats?.co2_saved || 0, unit: 'kg', icon: Leaf, color: 'text-green-600', bg: 'bg-green-50 dark:bg-green-900/20' },
    { label: 'Déchets sauvés', value: stats?.waste_saved || 0, unit: 'kg', icon: TrashIcon, color: 'text-blue-600', bg: 'bg-blue-50 dark:bg-blue-900/20' },
    { label: 'Repas sauvés', value: stats?.meals_rescued || 0, unit: '', icon: UtensilsCrossed, color: 'text-amber-600', bg: 'bg-amber-50 dark:bg-amber-900/20' },
    { label: 'Utilisateurs actifs', value: stats?.total_users || 0, unit: '', icon: Users, color: 'text-purple-600', bg: 'bg-purple-50 dark:bg-purple-900/20' },
  ]

  return (
    <div className="space-y-8">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => {
          const Icon = stat.icon
          return (
            <div key={stat.label} className={`${stat.bg} rounded-xl p-5 border border-gray-100 dark:border-gray-700`}>
              <div className="flex items-center gap-3 mb-2">
                <Icon className={`w-5 h-5 ${stat.color}`} />
                <span className="text-sm text-gray-500 dark:text-gray-400">{stat.label}</span>
              </div>
              <p className={`text-2xl font-bold ${stat.color}`}>
                {stat.value.toLocaleString()}{stat.unit}
              </p>
            </div>
          )
        })}
      </div>

      {/* Leaderboard */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-primary-600" />
          Classement
        </h2>
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {leaderboard.map((entry) => (
              <div key={entry.user_id} className="flex items-center gap-4 p-4 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors">
                <div className="w-8 text-center">
                  {entry.rank <= 3 ? (
                    <Medal className={`w-5 h-5 mx-auto ${
                      entry.rank === 1 ? 'text-yellow-500' :
                      entry.rank === 2 ? 'text-gray-400' :
                      'text-amber-700'
                    }`} />
                  ) : (
                    <span className="text-sm font-medium text-gray-400">#{entry.rank}</span>
                  )}
                </div>
                <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center overflow-hidden">
                  {entry.avatar_url ? (
                    <img src={entry.avatar_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Users className="w-4 h-4 text-gray-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{entry.user_name}</p>
                  <p className="text-xs text-gray-400">{entry.badges_count} badge{entry.badges_count > 1 ? 's' : ''}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-primary-600 dark:text-primary-400">{entry.points.toLocaleString()}</p>
                  <p className="text-xs text-gray-400">pts</p>
                </div>
              </div>
            ))}
            {leaderboard.length === 0 && (
              <div className="p-12 text-center text-gray-400">
                <Crown className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Aucun participant pour le moment</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ==================== User Achievements Tab ====================
const UserAchievementsTab = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<{ id: string; name: string; email: string }[]>([])
  const [selectedUser, setSelectedUser] = useState<{ id: string; name: string; email: string } | null>(null)
  const [userAchievements, setUserAchievements] = useState<UserAchievements | null>(null)
  const [searching, setSearching] = useState(false)
  const [loadingUser, setLoadingUser] = useState(false)

  const handleSearch = async () => {
    if (!searchQuery.trim()) return
    setSearching(true)
    try {
      const res = await gamificationService.searchUsers(searchQuery)
      setSearchResults(res.data)
    } catch {
      toast.error('Erreur lors de la recherche')
    } finally {
      setSearching(false)
    }
  }

  const selectUser = async (user: { id: string; name: string; email: string }) => {
    setSelectedUser(user)
    setLoadingUser(true)
    try {
      const res = await gamificationService.getUserAchievements(user.id)
      setUserAchievements(res.data)
    } catch {
      toast.error('Erreur lors du chargement des données')
    } finally {
      setLoadingUser(false)
    }
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Search Panel */}
      <div className="lg:col-span-1">
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Rechercher un utilisateur</h3>
          <div className="flex gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="Nom ou email..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
              />
            </div>
            <button onClick={handleSearch} disabled={searching} className="px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 text-sm">
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </button>
          </div>

          <div className="space-y-2">
            {searchResults.map((user) => (
              <button
                key={user.id}
                onClick={() => selectUser(user)}
                className={`w-full text-left p-3 rounded-lg text-sm transition-colors ${
                  selectedUser?.id === user.id
                    ? 'bg-primary-50 dark:bg-primary-900/30 border border-primary-200 dark:border-primary-700'
                    : 'bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <p className="font-medium text-gray-900 dark:text-white">{user.name}</p>
                <p className="text-xs text-gray-500">{user.email}</p>
              </button>
            ))}
            {searchResults.length === 0 && searchQuery && !searching && (
              <p className="text-sm text-gray-400 text-center py-4">Aucun utilisateur trouvé</p>
            )}
          </div>
        </div>
      </div>

      {/* User Details Panel */}
      <div className="lg:col-span-2">
        {loadingUser ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary-600" /></div>
        ) : userAchievements ? (
          <div className="space-y-6">
            {/* User Info */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                  <Users className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">{userAchievements.user_name}</h3>
                  <p className="text-sm text-gray-500">{userAchievements.user_email}</p>
                </div>
                <div className="ml-auto text-right">
                  <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">{userAchievements.total_points}</p>
                  <p className="text-xs text-gray-400">points total</p>
                </div>
              </div>
            </div>

            {/* Badges */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
                Badges ({userAchievements.badges.length})
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {userAchievements.badges.map((badge) => (
                  <div
                    key={badge.id}
                    className={`p-3 rounded-lg border text-center transition-all ${
                      badge.earned
                        ? 'bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-700'
                        : 'bg-gray-50 dark:bg-gray-700/30 border-gray-200 dark:border-gray-700 opacity-60'
                    }`}
                  >
                    <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-1">
                      <Award className={`w-4 h-4 ${badge.earned ? 'text-primary-600' : 'text-gray-300'}`} />
                    </div>
                    <p className={`text-xs font-medium ${badge.earned ? 'text-gray-900 dark:text-white' : 'text-gray-400'}`}>
                      {badge.name}
                    </p>
                    {badge.earned && badge.earned_at && (
                      <p className="text-[10px] text-gray-400 mt-0.5">
                        {new Date(badge.earned_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Achievements */}
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
                Succès ({userAchievements.achievements.length})
              </h4>
              <div className="space-y-3">
                {userAchievements.achievements.map((achievement) => (
                  <div
                    key={achievement.id}
                    className={`p-3 rounded-lg border transition-all ${
                      achievement.earned
                        ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700'
                        : 'bg-gray-50 dark:bg-gray-700/30 border-gray-200 dark:border-gray-700 opacity-60'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        achievement.earned ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-gray-100 dark:bg-gray-700'
                      }`}>
                        <Trophy className={`w-4 h-4 ${achievement.earned ? 'text-amber-600' : 'text-gray-300'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium ${achievement.earned ? 'text-gray-900 dark:text-white' : 'text-gray-400'}`}>
                          {achievement.name}
                        </p>
                        <p className="text-xs text-gray-500">{achievement.category}</p>
                      </div>
                      <span className={`text-xs font-medium ${achievement.earned ? 'text-primary-600' : 'text-gray-400'}`}>
                        +{achievement.points} pts
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400">
            <Search className="w-12 h-12 mb-3 opacity-50" />
            <p>Recherchez un utilisateur pour voir ses badges et succès</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default GamificationPage